/**
 * StudentTransferRequest — a student asking to move campus, programme or
 * study mode from their own portal. Staff still decide.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeftRight, ArrowRight, Loader2 } from 'lucide-react';

const STATUS_LABELS = {
  pending_receiving: 'Waiting for the receiving side',
  pending_registrar: 'Waiting for the registrar',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Withdrawn',
};

const readable = (v) => (v || '').replace(/_/g, ' ');

const StudentTransferRequest = ({ portalApi }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    to_campus_id: '', to_programme_id: '', to_level_id: '', to_study_mode: '', reason: '',
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['student-transfer'],
    queryFn: () => portalApi('/student/transfer'),
    retry: false,
  });

  const levels = useMemo(() => {
    const programme = (data?.programmes || []).find(
      (p) => p.id === (form.to_programme_id || data?.student?.programme_id));
    return programme?.levels || [];
  }, [data, form.to_programme_id]);

  const submit = useMutation({
    mutationFn: () => portalApi('/student/transfer', {
      method: 'POST', body: JSON.stringify(form),
    }),
    onSuccess: () => {
      toast.success('Your request has been sent to the school');
      setForm({ to_campus_id: '', to_programme_id: '', to_level_id: '', to_study_mode: '', reason: '' });
      queryClient.invalidateQueries({ queryKey: ['student-transfer'] });
    },
    onError: (e) => toast.error(e.message || 'Could not send the request'),
  });

  const withdraw = useMutation({
    mutationFn: (id) => portalApi(`/student/transfer/${id}/withdraw`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Request withdrawn');
      queryClient.invalidateQueries({ queryKey: ['student-transfer'] });
    },
    onError: (e) => toast.error(e.message || 'Could not withdraw'),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (error) {
    return (
      <Card><CardContent className="py-12 text-center" data-testid="student-transfer-error">
        <p className="text-sm text-slate-500">{error.message}</p>
      </CardContent></Card>
    );
  }

  const requests = data?.requests || [];
  const openRequest = requests.find((r) => r.status?.startsWith('pending'));
  const canAsk = data?.settings?.student_requests_enabled;

  return (
    <div className="space-y-6" data-testid="student-transfer">
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <ArrowLeftRight className="h-5 w-5 text-indigo-500" /> Transfer request
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Ask to move campus, programme or study mode. Everything you have already
          done stays on your record.
        </p>
      </div>

      {!canAsk && (
        <Card><CardContent className="py-10 text-center" data-testid="student-transfer-off">
          <p className="text-sm text-slate-500">
            Your school asks you to speak to the registrar about transfers.
          </p>
        </CardContent></Card>
      )}

      {canAsk && !openRequest && (
        <Card data-testid="student-transfer-form">
          <CardHeader>
            <CardTitle className="text-base">What would you like to change?</CardTitle>
            <CardDescription>
              Outstanding balance: {Number(data?.balance || 0).toLocaleString()}
              {data?.settings?.fee_policy === 'settle_first'
                ? ' · your school settles fees before a move'
                : ' · your balance moves with you'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Campus</Label>
                <Select value={form.to_campus_id}
                        onValueChange={(v) => setForm({ ...form, to_campus_id: v })}>
                  <SelectTrigger data-testid="student-transfer-campus">
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent>
                    {(data?.campuses || []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Programme</Label>
                <Select value={form.to_programme_id}
                        onValueChange={(v) => setForm({ ...form, to_programme_id: v, to_level_id: '' })}>
                  <SelectTrigger data-testid="student-transfer-programme">
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent>
                    {(data?.programmes || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {levels.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Level</Label>
                  <Select value={form.to_level_id}
                          onValueChange={(v) => setForm({ ...form, to_level_id: v })}>
                    <SelectTrigger data-testid="student-transfer-level">
                      <SelectValue placeholder="No change" />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Study mode</Label>
                <Select value={form.to_study_mode}
                        onValueChange={(v) => setForm({ ...form, to_study_mode: v })}>
                  <SelectTrigger data-testid="student-transfer-mode">
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent>
                    {(data?.study_modes || []).map((m) => (
                      <SelectItem key={m} value={m}>{readable(m)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Why are you asking?</Label>
              <Textarea rows={3} value={form.reason} data-testid="student-transfer-reason"
                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        placeholder="Tell the school what has changed" />
            </div>
            <Button onClick={() => submit.mutate()} disabled={submit.isPending}
                    data-testid="student-transfer-submit">
              {submit.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send my request
            </Button>
          </CardContent>
        </Card>
      )}

      {requests.length > 0 && (
        <Card data-testid="student-transfer-requests">
          <CardHeader><CardTitle className="text-base">Your requests</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {requests.map((r) => (
              <div key={r.id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0"
                   data-testid={`student-transfer-request-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    {STATUS_LABELS[r.status] || r.status}
                  </Badge>
                  <span className="text-sm text-slate-500">
                    {r.from.campus_name || '—'} · {r.from.programme_name || '—'}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-sm font-medium text-slate-800">
                    {r.to.campus_name || '—'} · {r.to.programme_name || '—'} ·{' '}
                    {readable(r.to.study_mode) || '—'}
                  </span>
                </div>
                {r.reason && <p className="mt-2 text-xs text-slate-500">“{r.reason}”</p>}
                {r.status?.startsWith('pending') && (
                  <Button size="sm" variant="ghost" className="mt-2"
                          data-testid={`student-transfer-withdraw-${r.id}`}
                          onClick={() => withdraw.mutate(r.id)}>Withdraw</Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StudentTransferRequest;
